<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed, nextTick } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import { useLeadStore } from "@/stores/leadStore";
import LeadsModal from "@/components/modal/leads-modal.vue";
import Modal from "bootstrap/js/dist/modal";

const title = "Leads";
const text = "CRM";
const text1 = "Leads List";

const store = useLeadStore();
const dateRangeInput = ref(null);

const editItem = ref(null);

// Filters
function booking_range(start, end) {
  store.filters.date_from = start.format("YYYY-MM-DD");
  store.filters.date_to = end.format("YYYY-MM-DD");
  store.fetchAll({ page: 1 });
}

onMounted(() => {
  const start = moment().subtract(6, "days");
  const end = moment();
  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range
    );
    booking_range(start, end);
  } else {
    store.ensureList({ page: 1 });
  }

  const el = document.getElementById("add_leads");
  el?.addEventListener("hidden.bs.modal", cleanupBackdrops);
  const el2 = document.getElementById("delete_leads");
  el2?.addEventListener("hidden.bs.modal", cleanupBackdrops);
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

const totalPages = computed(() => {
  const t = store.pagination.total || 0;
  const p = store.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

// function openAdd() {
//   const el = document.getElementById("add_leads");
//   Modal.getOrCreateInstance(el)?.show();
// }

function openAdd() {
  editItem.value = null; // important: null means "create"
  nextTick(() => {
    const el = document.getElementById("add_leads");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function openEdit(lead) {
  editItem.value = lead;
  nextTick(() => {
    const el = document.getElementById("add_leads");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function openDelete(lead) {
  editItem.value = lead;
  nextTick(() => {
    const el = document.getElementById("delete_leads");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function onSaved() {
  cleanupBackdrops();
  editItem.value = null;
  store.fetchAll({ page: 1 });
}

function cleanupBackdrops() {
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("padding-right");
  document.body.style.removeProperty("overflow");
}

function exportCsv() {
  const header = [
    "Name",
    "Company",
    "Phone",
    "Email",
    "Pipeline",
    "Stage",
    "Owner",
    "Tags",
    "Created At",
  ];
  const body = (Array.isArray(store.items) ? store.items : []).map((lead) => [
    lead.name || "",
    lead.company_name || "",
    lead.phone || "",
    lead.email || "",
    lead.pipeline?.name || "",
    lead.stage?.name || "",
    [lead.owner?.first_name, lead.owner?.last_name].filter(Boolean).join(" ") ||
      lead.owner?.email ||
      "",
    Array.isArray(lead.tags) ? lead.tags.join(" | ") : "",
    lead.created_at || "",
  ]);

  const csv = [header, ...body]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `crm-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// onMounted(() => {

//   const el = document.getElementById("lead_modal");
//   el?.addEventListener("hidden.bs.modal", cleanupBackdrops);
//   store.fetchAll();
// });
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          

          <div class="me-2 mb-2">
            <button
              class="btn btn-white d-inline-flex align-items-center"
              @click="exportCsv"
              v-can="'Lead.Export'"
            >
              <i class="ti ti-file-type-csv me-1"></i>Export CSV
            </button>
          </div>

          <div class="mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAdd"
              v-can="'Lead.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Lead
            </a>
          </div>

          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Leads List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon"
                  ><i class="ti ti-chevron-down"></i
                ></span>
              </div>
            </div>

            <div class="me-3">
              <input
                v-model.trim="store.filters.q"
                class="form-control"
                placeholder="Search name / email / phone"
                @keyup.enter="store.fetchAll({ page: 1 })"
              />
            </div>

            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
                >Tags</a
              >
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.tags = 'Closed';
                      store.fetchAll({ page: 1 });
                    "
                    >Closed</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.tags = 'Contacted';
                      store.fetchAll({ page: 1 });
                    "
                    >Contacted</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.tags = 'Lost';
                      store.fetchAll({ page: 1 });
                    "
                    >Lost</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.tags = 'Not Contacted';
                      store.fetchAll({ page: 1 });
                    "
                    >Not Contacted</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <table class="table datatable thead-light">
              <thead>
                <tr>
                  <th></th>
                  <th>Lead Name</th>
                  <th>Company Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Tags</th>
                  <th>Created Date</th>
                  <th>Lead Owner</th>
                  <th></th>
                </tr>
              </thead>
              <tbody v-if="!store.loading">
                <tr v-for="lead in store.items" :key="lead.id">
                  <td><input type="checkbox" /></td>
                  <td>
                    <h6 class="fs-14 fw-medium">
                      <router-link
                        :to="{
                          name: 'crm.leads.details',
                          params: { id: lead.id },
                        }"
                      >
                        {{ lead.name }}
                      </router-link>
                    </h6>
                  </td>
                  <td>{{ lead.company_name || "-" }}</td>
                  <td>{{ lead.phone || "-" }}</td>
                  <td>{{ lead.email || "-" }}</td>
                  <td>
                    <span
                      v-for="t in lead.tags || []"
                      :key="t"
                      class="badge me-1"
                      :class="{
                        'badge-success-transparent': t === 'Closed',
                        'badge-purple-transparent': t === 'Contacted',
                        'badge-danger-transparent': t === 'Lost',
                        'badge-warning-transparent': t === 'Not Contacted',
                      }"
                      >{{ t }}</span
                    >
                    <span
                      v-if="!lead.tags || !lead.tags.length"
                      class="text-muted"
                      >-</span
                    >
                  </td>
                  <td>{{ (lead.created_at || "").slice(0, 10) }}</td>
                  <td>
                    {{
                      lead.owner?.first_name
                        ? lead.owner.first_name +
                          " " +
                          (lead.owner.last_name || "")
                        : "-"
                    }}
                  </td>
                  <td>
                    <div class="action-icon d-inline-flex">
                      <router-link
                        :to="{
                          name: 'crm.leads.details',
                          params: { id: lead.id },
                        }"
                        class="me-2"
                        v-can="'Lead.Read'"
                      >
                        <i class="ti ti-eye"></i>
                      </router-link>

                      <a href="javascript:void(0);" @click="openEdit(lead)"
                        v-can="'Lead.Edit'"
                        ><i class="ti ti-edit"></i
                      ></a>
                      <a href="javascript:void(0);" @click="openDelete(lead)"
                        v-can="'Lead.Delete'"
                        ><i class="ti ti-trash"></i
                      ></a>
                    </div>
                  </td>
                </tr>
                <tr v-if="!store.items.length">
                  <td colspan="9" class="text-center text-muted p-4">
                    No leads found
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="9" class="p-4">Loading...</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="p-3 d-flex justify-content-between align-items-center">
            <div>
              Showing page {{ store.pagination.current_page }} of
              {{ totalPages }}
            </div>
            <div class="btn-group">
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page <= 1"
                @click="
                  store.fetchAll({ page: store.pagination.current_page - 1 })
                "
              >
                Prev
              </button>
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page >= totalPages"
                @click="
                  store.fetchAll({ page: store.pagination.current_page + 1 })
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>

  <leads-modal :edit-item="editItem" @saved="onSaved" />
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control,
  .dq-list-toolbar .input-group,
  .dq-list-toolbar .btn-group {
    width: 100%;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


